import { useForm } from '@folklore/forms';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { ForwardedRef, ReactNode, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AuthResponse, useAuthIdentify, useAuthLogin, useAuthSendToken } from '../../hooks/useAuth';
import { mergeRefs } from '../../lib/utils';

import { useSetting } from '../../contexts/SiteContext';
import FormButton from '../buttons/FormButton';
import SsoButton from '../buttons/SsoButton';
import ContactField from '../fields/ContactField';
import FormStatus from '../partials/FormStatus';
import LineSpacer from '../partials/LineSpacer';
import FormControl from './FormControl';
import VerifyTokenForm from './VerifyTokenForm';

import styles from '../../styles/forms/identify-form.module.css';

export interface IdentifyFormProps {
    source?: string;
    placeholder?: string | null;
    label?: ReactNode;
    alwaysVertical?: boolean;
    ssoTheme?: 'dark' | 'light';
    withVerification?: boolean;
    submitButtonLabel?: ReactNode;
    className?: string | null;
    onFocus?: (() => void) | null;
    onBlur?: (() => void) | null;
    onVerify?: ((method: 'email' | 'phone') => void) | null;
    onComplete?: ((user: AuthResponse) => void) | null;
    onChange?: ((value: { email?: string; phone?: string; field?: string } | null) => void) | null;
    onClickSso?: ((provider: string) => void) | null;
}

function IdentifyForm({
    placeholder = null,
    source = 'identify',
    label = null,
    alwaysVertical = false,
    ssoTheme = 'dark',
    withVerification = false,
    submitButtonLabel = null,
    className = null,
    onFocus = null,
    onBlur = null,
    onVerify = null,
    onComplete = null,
    onChange = null,
    onClickSso: customOnClickSso = null,
    ref: formRef = null,
}: IdentifyFormProps & { ref?: ForwardedRef<HTMLFormElement> | null }) {
    const ssoButtons = useSetting('ssoButtons', ['google', 'facebook']);
    const intl = useIntl();
    const { sendTokenAsync } = useAuthSendToken();
    const { identifyAsync } = useAuthIdentify();
    const { loginAsync } = useAuthLogin();
    const formFields = ['field', 'phone', 'email'];
    const tracking = useTracking();

    const [fieldType, setFieldType] = useState<'email' | 'tel'>('email');
    const [verifyToken, setVerifyToken] = useState(false);
    const method = fieldType === 'tel' ? 'phone' : 'email';

    const postForm = (formAction: string, { field }: { field: string }) =>
        withVerification && !verifyToken
            ? sendTokenAsync({
                  [method]: field,
              })
            : identifyAsync({
                  source,
                  [method]: field,
              });
    const onFormComplete = (newUser: AuthResponse) => {
        if (withVerification && !verifyToken) {
            setVerifyToken(true);
            if (onVerify !== null) {
                onVerify(method);
            }
            tracking.trackEvent('Auth', 'send_token', method);
            return;
        }
        if (onComplete !== null) {
            onComplete(newUser);
        }
        tracking.trackEvent('Auth', 'identify', method);
    };
    const onSsoComplete = (provider: string, newUser: AuthResponse) => {
        if (onComplete !== null) {
            onComplete(newUser);
        }
        tracking.trackEvent('Auth', 'identify', provider);
    };

    const onClickSso = (provider: string) => {
        if (customOnClickSso !== null) {
            customOnClickSso(provider);
        }
        tracking.trackEvent('Auth', 'click_sso', provider);
    };

    const { submit, value, fields, setValue, onSubmit, status, errors, setErrors } = useForm({
        fields: formFields,
        postForm,
        onComplete: onFormComplete,
    });

    const { errors: fieldErrors = null } = fields[method] || {};
    const onFieldTypeChange = (newType: 'email' | 'tel') => {
        setFieldType(newType);
        setErrors(null);
    };

    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (errors !== null) {
            // inputRef.current?.focus();
        }
    }, [errors]);

    useEffect(() => {
        if (onChange !== null) {
            onChange(value);
        }
    }, [value]);

    const onClickCancelVerify = () => {
        setVerifyToken(false);
        setValue({
            field: null,
        });
        tracking.trackEvent('Auth', 'cancel_verify', method);
    };

    const {
        field: { value: fieldValue },
    } = fields;

    const postVerifyToken = (formAction, { token }) =>
        loginAsync({
            [method]: fieldValue,
            token,
        });

    if (verifyToken) {
        return (
            <VerifyTokenForm
                postForm={postVerifyToken}
                method={method}
                autoFocus
                className={classNames([styles.container, className])}
                onClickCancel={onClickCancelVerify}
                onComplete={onFormComplete}
            />
        );
    }

    return (
        <form
            className={classNames([
                styles.container,
                {
                    'was-validated': errors !== null,
                    [styles.alwaysVertical]: alwaysVertical,
                },
                className,
            ])}
            onSubmit={onSubmit}
            ref={mergeRefs(formRef, (ref) => {
                if (ref !== null) {
                    ref.submit = submit;
                }
            })}
        >
            <FormControl
                className={styles.formControl}
                label={label}
                {...fields[method]}
                withoutErrors
            >
                <div
                    className={classNames([
                        styles.fieldGroup,
                        {
                            [styles.hasStatus]: status !== null,
                        },
                    ])}
                >
                    <ContactField
                        {...fields.field}
                        ref={inputRef}
                        required
                        withBorder
                        errors={fieldErrors}
                        className={styles.field}
                        placeholder={
                            placeholder ||
                            intl.formatMessage({
                                defaultMessage: 'Inscrivez votre # de tel ou courriel',
                                description: 'Field placeholder',
                            })
                        }
                        onTypeChange={onFieldTypeChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                    />
                    <FormStatus status={status} className={styles.status} />
                </div>
                <FormButton type="submit" disabled={status === 'loading'} className={styles.button}>
                    {submitButtonLabel || (
                        <FormattedMessage defaultMessage="Continuer" description="Button label" />
                    )}
                </FormButton>
            </FormControl>

            <LineSpacer className={styles.spacer}>
                <FormattedMessage defaultMessage="ou" description="Spacer label" />
            </LineSpacer>
            <div className={styles.sso}>
                {ssoButtons.map((provider) => (
                    <SsoButton
                        key={`sso-${provider}`}
                        provider={provider}
                        source={source}
                        theme={ssoTheme}
                        // size="medium"
                        className={styles.ssoButton}
                        onClick={() => onClickSso(provider)}
                        onComplete={(newUser) => onSsoComplete(provider, newUser)}
                    />
                ))}
            </div>
        </form>
    );
}

export default IdentifyForm;
